import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Checkbox, Empty, List, Space, Spin, Tag, Typography, message } from "antd";

import { type SimulatorTabProps } from "../types";
import {
  allDishKeys,
  eatenDishKeys,
  resolveNutritionDay,
  toSimulatorMeals,
  trackNutritionDay,
} from "../writes/nutritionWrites";

const SimulatorNutritionTab = ({ target, date, createdBy, onWritten }: SimulatorTabProps) => {
  const queryClient = useQueryClient();
  const dayKey = ["simulator", "nutrition", target.id, date];

  const nutritionDay = useQuery({
    queryKey: dayKey,
    queryFn: () => resolveNutritionDay(target.id, target.activeMealsPlanId, date),
  });

  const track = useMutation({
    mutationFn: (eatenKeys: Set<string>) => {
      if (!nutritionDay.data) {
        throw new Error("Brak dnia diety do zapisania");
      }
      return trackNutritionDay({
        nutritionDay: nutritionDay.data,
        traineeId: target.id,
        userId: target.userId,
        createdBy,
        date,
        eatenKeys,
        mode: "single",
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dayKey });
      onWritten();
    },
    onError: (error: Error) => void message.error(error.message),
  });

  if (nutritionDay.isLoading) {
    return <Spin />;
  }

  if (nutritionDay.isError) {
    return <Alert type="error" showIcon message={(nutritionDay.error as Error).message} />;
  }

  if (!nutritionDay.data) {
    return (
      <Empty
        description="Brak dnia diety — podopieczna nie ma aktywnego planu żywieniowego ani wpisu w trackerze na ten dzień"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const { source, day, trackedMeals, totalMeals } = nutritionDay.data;
  const meals = toSimulatorMeals(day);
  const currentKeys = eatenDishKeys(day);

  const toggleDish = (dishKeyValue: string, eaten: boolean) => {
    const next = new Set(currentKeys);
    if (eaten) {
      next.add(dishKeyValue);
    } else {
      next.delete(dishKeyValue);
    }
    track.mutate(next);
  };

  const toggleMeal = (mealDishKeys: string[], eaten: boolean) => {
    const next = new Set(currentKeys);
    for (const key of mealDishKeys) {
      if (eaten) {
        next.add(key);
      } else {
        next.delete(key);
      }
    }
    track.mutate(next);
  };

  return (
    <Space direction="vertical" size={16} className="w-full">
      <Alert
        type="info"
        showIcon
        message="Posiłek liczy się, gdy ma choć jedno danie oznaczone jako zjedzone"
        description="Tyle właśnie wpada do client_activity jako mealTracked i tyle widzi kategoria mealsTracked w zadaniach wyzwania — odhaczenie kolejnych dań w tym samym posiłku niczego już nie dokłada."
      />

      <Card
        size="small"
        title={
          <Space>
            <span>{day.name || "Dzień diety"}</span>
            <Tag color={source === "tracked" ? "green" : "default"}>
              {source === "tracked" ? "z trackera klientki" : "z planu żywieniowego"}
            </Tag>
          </Space>
        }
        extra={
          <Space>
            <Button
              size="small"
              type="primary"
              loading={track.isLoading}
              onClick={() => track.mutate(allDishKeys(day))}
            >
              Odhacz wszystko
            </Button>
            <Button size="small" danger loading={track.isLoading} onClick={() => track.mutate(new Set())}>
              Wyczyść
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={12} className="w-full">
          <Typography.Text>
            Posiłki z odhaczonym daniem: <strong>{trackedMeals}</strong> z {totalMeals}
          </Typography.Text>

          {meals.length ? (
            <List
              size="small"
              dataSource={meals}
              renderItem={(meal) => {
                const mealKeys = meal.dishes.map((dish) => dish.key);
                const allEaten = mealKeys.length > 0 && meal.dishes.every((dish) => dish.eaten);
                return (
                  <List.Item>
                    <Space direction="vertical" size={4} className="w-full">
                      <Space size={8}>
                        <Checkbox
                          checked={allEaten}
                          indeterminate={!allEaten && meal.tracked}
                          disabled={track.isLoading || mealKeys.length === 0}
                          onChange={(event) => toggleMeal(mealKeys, event.target.checked)}
                        >
                          <Typography.Text strong>{meal.name}</Typography.Text>
                        </Checkbox>
                        {meal.tracked ? <Tag color="green">liczy się</Tag> : null}
                      </Space>

                      <Space direction="vertical" size={0} className="pl-6">
                        {meal.dishes.length ? (
                          meal.dishes.map((dish) => (
                            <Checkbox
                              key={dish.key}
                              checked={dish.eaten}
                              disabled={track.isLoading}
                              onChange={(event) => toggleDish(dish.key, event.target.checked)}
                            >
                              {dish.name}
                            </Checkbox>
                          ))
                        ) : (
                          <Typography.Text type="secondary">Posiłek bez dań</Typography.Text>
                        )}
                      </Space>
                    </Space>
                  </List.Item>
                );
              }}
            />
          ) : (
            <Empty description="Dzień nie ma posiłków" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}

          {source === "mealsPlan" ? (
            <Typography.Text type="secondary">
              Ten dzień nie istnieje jeszcze w trackerze — pierwszy zapis utworzy go z planu, dokładnie jak odhaczenie
              po stronie klientki.
            </Typography.Text>
          ) : null}
        </Space>
      </Card>
    </Space>
  );
};

export default SimulatorNutritionTab;
